/** @format */
/** @format */

import React, { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  _$,
  Select,
  Option,
  Table,
  TableColumn,
  Pagination,
  Tag,
  Message,
  Ellipsis,
  dayjs
} from '@king-design/react'
import { CustomDatePicker } from 'src/components'
import {
  MonitorSearch,
  DeleteConfirm,
  TablePage,
  TableSearch,
  parseTableSearchParams
} from 'src/components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import { PageProps, initPage } from 'src/utils/interface'
import { useHistory, useLocation } from 'react-router-dom'
import { useAsyncFn } from 'react-use'
import { getPrometheusAlertNotific, getPrometheusAlertPolicyList } from 'src/api/ApiPrometheusAlarm'
import { merge, unset } from 'lodash'
import { notificationResultScheme } from 'src/components/AlarmReciver/new'
import { urlNoticeResultScheme } from 'src/components/AlarmCallBack/new'

interface SearchProps extends PageProps {
  Instance: string
  PolicyName: string
  RecordType: 0 | 1 | 2
  Time: string | [number, number]
}

const initSearch: SearchProps = {
  Instance: '',
  PolicyName: '',
  RecordType: 0,
  Time: '1week',
  ...initPage
}

const NotificationTypeEnum = {
  0: '全部',
  1: '触发',
  2: '恢复'
}
const PrometheusAlarmNotificRecord: FC<PrometheusAlarmTabProps> = ({
  className,
  region,
  instances
}) => {
  const history = useHistory()
  const location = useLocation()
  const [search, setSearch] = useState<SearchProps>(
    () =>
      parseTableSearchParams<SearchProps>(location.search) || {
        ...initSearch,
        Time: [dayjs().subtract(1, 'week').unix(), dayjs().unix()]
      }
  )
  const [{ value: tableData }, fetch] = useAsyncFn(
    async (params?) => {
      console.log('search', search)
      const mergeParams = merge({}, search, params)
      mergeParams.StartTime = mergeParams.Time[0] * 1000
      mergeParams.EndTime = mergeParams.Time[1] * 1000
      mergeParams.Region = region
      unset(mergeParams, 'Time')
      if (mergeParams.StartTime && mergeParams.EndTime) {
        const result = await getPrometheusAlertNotific(mergeParams)
        return {
          data: result?.Records || [],
          total: result?.TotalCount || 0
        }
      }
    },
    [search, region]
  )

  useEffect(() => {
    fetch()
  }, [search, region])

  const toPolicyPage = async (policyID, ruleName?) => {
    if (policyID) {
      const result = await getPrometheusAlertPolicyList({
        PolicyID: parseInt(policyID)
      })
      if (result?.Polices?.length) {
        let url = `/monitor/prometheusmonitor/alarm/edit?tab=notific&page=policy&policyid=${policyID}`
        if (ruleName) {
          url += `&rulename=${ruleName}`
        }
        history.push(url)
      } else {
        Message.error(_$('策略不存在'))
      }
    } else {
      Message.error(_$('策略ID为空'))
    }
  }

  return (
    <div className={className}>
      <TableSearch params={search}>
        <div className="search_item">
          <CustomDatePicker
            value={search.Time}
            onChange={date => {
              setSearch({
                ...search,
                Page: 1,
                Time: date
              })
            }}
          />
        </div>
        <div className="ksc-space">
          <div className="search_item">
            <Select
              value={search.Instance}
              clearable
              filterable
              placeholder={_$('请选择关联资源')}
              slotPrefix={`${_$('关联资源')}：`}
              onChangeValue={value => {
                setSearch({
                  ...search,
                  Page: 1,
                  Instance: value
                })
              }}
            >
              {instances?.map(item => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="search_item">
            <Select
              value={search.RecordType}
              placeholder={_$('请选择通知类型')}
              slotPrefix={`${_$('通知类型')}：`}
              onChangeValue={value => {
                setSearch({
                  ...search,
                  Page: 1,
                  RecordType: value
                })
              }}
            >
              {Object.entries(NotificationTypeEnum).map(([value, name]) => {
                return (
                  <Option key={value} value={parseInt(value)}>
                    {_$(name)}
                  </Option>
                )
              })}
            </Select>
          </div>
          <div className="search_item">
            <MonitorSearch
              placeholder={_$('请输入策略名称，支持模糊搜索')}
              value={search.PolicyName}
              prefix={`${_$('策略名称')}：`}
              onChange={value => {
                setSearch({
                  ...search,
                  Page: 1,
                  PolicyName: value
                })
              }}
            />
          </div>
        </div>
      </TableSearch>
      <Table
        stickHeader={50}
        data={tableData?.data || []}
        animation={false}
        rowKey={data => data.ID}
        checkType="none"
      >
        <TableColumn
          key="InstanceID"
          title={_$('关联资源')}
          slotTemplate={([data]) => {
            return data.InstanceID ? (
              <>
                <p>{data.InstanceName}</p>
                <Ellipsis className="table_text" maxLines={2} hoverable>
                  {data.InstanceID}
                </Ellipsis>
              </>
            ) : null
          }}
        ></TableColumn>
        <TableColumn
          key="RuleName"
          title={_$('策略 / 规则')}
          slotTemplate={([data]) => {
            return (
              <a
                onClick={() => {
                  toPolicyPage(data.PolicyID, data.RuleName)
                }}
              >{`${data.PolicyName} / ${data.RuleName}`}</a>
            )
          }}
        />
        <TableColumn
          key="RecordType"
          title={_$('通知类型')}
          width="100"
          slotTemplate={([data]) => {
            return data.RecordType === 1 ? (
              <Tag type="danger">{_$('触发')}</Tag>
            ) : (
              <Tag type="default">{_$('恢复')}</Tag>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Labels"
          title={_$('标签')}
          slotTemplate={([data]) => {
            if (!data.Labels || !Array.isArray(data.Labels)) return '-'
            return (
              <div className="label-tags">
                {data.Labels.map(label => {
                  if (label.Name === 'severity') {
                    return (
                      <p key={label.Name}>
                        {_$('告警等级（severity）')}= {label.Value}
                      </p>
                    )
                  } else {
                    return (
                      <p key={label.Name}>
                        {label.Name} = {label.Value}
                      </p>
                    )
                  }
                })}
              </div>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Summary"
          title={_$('告警摘要')}
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Summary}
              </Ellipsis>
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title={_$('告警内容')}
          slotTemplate={([data]) => {
            return (
              <Ellipsis maxLines={2} hoverable>
                {data.Description}
              </Ellipsis>
            )
          }}
        ></TableColumn>

        <TableColumn
          key="Recipients"
          title={_$('通知对象')}
          width="100"
          slotTemplate={([data]) => {
            const { NotificationTargets } = data
            const { Recipients = [], Webhooks = [] } = NotificationTargets || {}
            return Recipients?.length || Webhooks?.length ? (
              <div className="ksc-space">
                {Recipients?.length ? (
                  <DeleteConfirm
                    title={_$('联系人')}
                    content={
                      <>
                        <TablePage
                          tableProps={{
                            checkType: 'none',
                            rowKey: data => data.ContactID,
                            scheme: notificationResultScheme,
                            data: Recipients
                          }}
                        ></TablePage>
                      </>
                    }
                  >
                    <a>{_$('联系人')}</a>
                  </DeleteConfirm>
                ) : null}
                {Webhooks?.length ? (
                  <DeleteConfirm
                    title={_$('回调')}
                    content={
                      <TablePage
                        tableProps={{
                          checkType: 'none',
                          rowKey: data => data.ContactID,
                          scheme: urlNoticeResultScheme,
                          data: Webhooks
                        }}
                      ></TablePage>
                    }
                  >
                    <a>{_$('回调')}</a>
                  </DeleteConfirm>
                ) : null}
              </div>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
        <TableColumn key="NotifiedAt" title={_$('发送时间')} width="100"></TableColumn>
      </Table>
      {tableData !== undefined ? (
        <Pagination
          value={search.Page}
          limit={search.PageSize}
          total={tableData?.total || 0}
          size="small"
          onChangeValue={value => {
            setSearch({
              ...search,
              Page: value
            })
          }}
          onChangeLimit={limit => {
            setSearch({
              ...search,
              PageSize: limit
            })
          }}
        />
      ) : null}
    </div>
  )
}

export default styled(PrometheusAlarmNotificRecord)`
  .ion-refresh {
    margin-left: 4px;
  }
  .table-search {
    display: block;
  }
`
